import { supabaseClient } from "@/lib/database/supabase";
import { TablesUpdate } from "@shared/lib/database/database.types";
import { PlayerSummary, StatusSummaryWithPlayer } from "@shared/lib/database/query.types";
import { PostgrestError } from "@supabase/supabase-js";
import { defineStore } from "pinia";
import { ref } from "vue";

export default defineStore("StatusStore", () => {
  const statusMap = ref<Map<string, StatusSummaryWithPlayer | null>>(new Map());

  async function fetchPlayerWithStatus (runId: string, playerId: string): Promise<[PostgrestError | null, {
    player: PlayerSummary,
    status: StatusSummaryWithPlayer | null,
  } | null]> {
    const {
      data: playerWithStatuses,
      error,
    } = await supabaseClient.from("players")
      .select("id, name, theme, avatar_storage_path, statuses(run_id, player_id, probability, note, created_at, updated_at)")
      .eq("id", playerId)
      .eq("statuses.run_id", runId)
      .maybeSingle();

    if (!playerWithStatuses) return [error, null];

    const {
      statuses: [status],
      ...player
    } = playerWithStatuses;

    const statusWithPlayer = status
      ? {
        ...status,
        player,
      }
      : null;

    if (statusWithPlayer) {
      statusMap.value.set(getIdForStatus(runId, playerId), statusWithPlayer);
    } else {
      statusMap.value.delete(getIdForStatus(runId, playerId));
    }

    return [
      error,
      {
        player,
        status: statusWithPlayer,
      },
    ];
  }

  async function upsertStatus (runId: string, playerId: string, payload: TablesUpdate<"statuses">): Promise<[Error | null, StatusSummaryWithPlayer | null]> {
    const {
      data: status,
      error,
    } = await supabaseClient
      .from("statuses")
      .upsert({
        run_id: runId,
        player_id: playerId,
        ...payload,
      })
      .select("*, player:players(id, name, theme, avatar_storage_path)") // TODO
      .maybeSingle();

    if (status) {
      updateStatus(runId, playerId, status);
    }

    return [error, status];
  }

  async function deleteStatus (runId: string, playerId: string) {
    const {
      error,
    } = await supabaseClient
      .from("statuses")
      .delete()
      .match({
        run_id: runId,
        player_id: playerId,
      });

    if (!error) {
      removeStatus(runId, playerId);
    }

    return [error];
  }

  function getStatus (runId: string, playerId: string) {
    return statusMap.value.get(getIdForStatus(runId, playerId)) || null;
  }

  function updateStatus (runId: string, playerId: string, status: StatusSummaryWithPlayer) {
    const id = getIdForStatus(runId, playerId);
    const updatedStatus = {
      ...statusMap.value.get(id),
      ...status,
    };

    statusMap.value.set(id, updatedStatus);

    return updatedStatus;
  }

  function removeStatus (runId: string, playerId: string) {
    statusMap.value.delete(getIdForStatus(runId, playerId));
  }

  function getIdForStatus (runId: string, playerId: string) {
    return [runId, playerId].join(",");
  }

  return {
    getStatus,
    fetchPlayerWithStatus,
    upsertStatus,
    deleteStatus,
    updateStatus,
    removeStatus,
  };
});
